/**
 * Focus-list resolution for Part D (pilot) and Part C (instructor).
 *
 * GAT-04: grade 3 Adequate meets the required standard and is the default
 * target. It must not be grouped with grades 1 and 2, and it appears in no
 * deficiency count. A client-derived focus list therefore selects grade <= 2
 * only.
 *
 * The authoritative list is computed on the server, by the two views migration
 * 258 creates over the LIVE assessments_pilot rows (G3-06). Its rule, decided
 * on 17 August 2026: a competency is a focus competency when its grade is 2 or
 * lower, OR when its grade is exactly 3 and its captured tem_outcome resolves,
 * through the framework version stamped on that grading, to one of the worst
 * two positions of the tem_outcome dimension. A null tem_outcome and a
 * historical row never qualify under the second clause.
 *
 * The client cannot reproduce that: it would have to resolve a registry scale
 * per grading and would drift the moment a tenant version reordered its codes.
 * So a server list is rendered verbatim, and only in its absence does the
 * client fall back to the grade <= 2 selection below. The fallback is not the
 * same list, and it is not meant to be; it is the largest subset of the rule
 * that can be decided from a grade alone.
 */

/** Highest grade a client-derived focus list may include. GAT-04. */
export const CLIENT_FOCUS_MAX_GRADE = 2;

/** Minimal shape a client-side focus candidate must have. */
export interface FocusCandidate {
  score?: number | null;
}

export interface ClientFocusOptions {
  /** Order worst grade first. Off by default so callers keep their own order. */
  sortWorstFirst?: boolean;
  /** Cap the list length. Unlimited by default. */
  max?: number;
}

/**
 * True when a grade belongs in a client-derived focus list.
 *
 * Null, undefined and non-numeric grades are excluded: an ungraded competency
 * is not evidence of a shortfall.
 */
export function isClientFocusGrade(score: number | null | undefined): boolean {
  return typeof score === 'number' && Number.isFinite(score) && score <= CLIENT_FOCUS_MAX_GRADE;
}

/**
 * Client fallback focus list: grades 1 and 2 only. Grade 3 never appears.
 */
export function deriveClientFocusList<T extends FocusCandidate>(
  assessments: readonly T[] | null | undefined,
  options: ClientFocusOptions = {}
): T[] {
  const selected = (assessments ?? []).filter((a) => isClientFocusGrade(a?.score));

  const ordered = options.sortWorstFirst
    ? [...selected].sort((a, b) => (a.score as number) - (b.score as number))
    : selected;

  return typeof options.max === 'number' ? ordered.slice(0, options.max) : ordered;
}

export type ResolvedFocusList<TServer, TItem> =
  | { source: 'server'; items: TServer[] }
  | { source: 'client'; items: TItem[] };

/**
 * Render the server focus list verbatim when the payload carries one;
 * otherwise fall back to the client grade <= 2 derivation.
 *
 * An empty server array is still a server result: it means the server found no
 * focus competency, which is a different statement from "no server ran".
 * Only null or undefined trigger the fallback.
 */
export function resolveFocusList<TServer, TItem extends FocusCandidate>(
  serverFocusList: readonly TServer[] | null | undefined,
  assessments: readonly TItem[] | null | undefined,
  options: ClientFocusOptions = {}
): ResolvedFocusList<TServer, TItem> {
  if (serverFocusList != null) {
    return { source: 'server', items: [...serverFocusList] };
  }
  return { source: 'client', items: deriveClientFocusList(assessments, options) };
}

// ---------------------------------------------------------------------------
// The server list (migration 258, G3-06)
// ---------------------------------------------------------------------------

/** The view every surface reads the focus list from. Migration 258. */
export const SERVER_FOCUS_LIST_VIEW = 'v_assessment_focus_list';

/** Why the server put a competency in the list. Migration 258. */
export type ServerFocusReason = 'grade_at_or_below_2' | 'grade_3_deficiency_evidence';

/** One row of v_assessment_focus_list. */
export interface ServerFocusEntry {
  session_id: string;
  /** Null on a single-pilot session; the set key alongside session_id. */
  pilot_role: string | null;
  competency: string;
  score: number | null;
  focus_reason: ServerFocusReason;
  /** The captured code, for surfaces that want to show why. */
  tem_outcome: string | null;
  /** Its position in the stamped version's scale, never a rank. */
  tem_position: number | null;
  framework_version: string | null;
  /** 1-based render order within the assessment set, worst grade first. */
  focus_rank: number;
}

/** A live grading row, as the surfaces already hold it. */
export interface FocusRow extends FocusCandidate {
  competency?: string | null;
  pilot_role?: string | null;
}

/**
 * The join key. A dual-pilot session carries two independent focus lists, so
 * the competency code alone is ambiguous there; pilot_role completes it, and a
 * null role (every single-pilot session) is a key of its own rather than a
 * wildcard.
 */
function focusKey(
  competency: string | null | undefined,
  pilotRole: string | null | undefined,
): string {
  return `${competency ?? ''}::${pilotRole ?? ''}`;
}

export type ResolvedFocusRows<T> = { source: 'server' | 'client'; items: T[] };

/**
 * Resolve the rows a surface should render as its focus list.
 *
 * With a server list, the caller's own grading rows are returned in the
 * server's order, so every surface keeps rendering the row objects it already
 * has (grade, analysis, goals, evidence) while the server decides membership
 * and order. A server entry with no matching row is dropped: on a dual-pilot
 * session that is the other pilot's competency, and on any session it is a row
 * the caller filtered out for its own reasons.
 *
 * Without one, the client falls back to grades 1 and 2, and `options` apply to
 * that fallback alone. A server list is never re-sorted and never capped:
 * rendering it verbatim is the requirement, and a cap belongs to the surface.
 */
export function resolveFocusRows<T extends FocusRow>(
  serverFocusList: readonly ServerFocusEntry[] | null | undefined,
  assessments: readonly T[] | null | undefined,
  options: ClientFocusOptions = {}
): ResolvedFocusRows<T> {
  const resolved = resolveFocusList<ServerFocusEntry, T>(serverFocusList, assessments, options);

  if (resolved.source === 'client') {
    return { source: 'client', items: resolved.items };
  }

  const byKey = new Map<string, T>();
  for (const row of assessments ?? []) {
    if (!row) continue;
    byKey.set(focusKey(row.competency, row.pilot_role), row);
  }

  const items: T[] = [];
  for (const entry of [...resolved.items].sort((a, b) => a.focus_rank - b.focus_rank)) {
    const row = byKey.get(focusKey(entry.competency, entry.pilot_role));
    if (row) items.push(row);
  }

  return { source: 'server', items };
}

// ---------------------------------------------------------------------------
// Reading the view
// ---------------------------------------------------------------------------
// The narrowest surface of a Supabase (PostgREST) client this needs. All three
// platforms' clients satisfy it structurally, so nothing here couples to a
// @supabase/supabase-js version and a test can pass a hand-rolled mock. Same
// approach as the framework loader.

export interface FocusListQueryResult {
  data: unknown;
  error: { message: string } | null;
}

export interface FocusListSelectBuilder extends PromiseLike<FocusListQueryResult> {
  eq(column: string, value: unknown): FocusListSelectBuilder;
  order(column: string, options?: { ascending?: boolean }): FocusListSelectBuilder;
}

export interface FocusListDataClient {
  from(table: string): { select(columns: string): FocusListSelectBuilder };
}

const FOCUS_LIST_COLUMNS =
  'session_id, pilot_role, competency, score, focus_reason, tem_outcome, tem_position, framework_version, focus_rank';

/**
 * Read one session's server focus list.
 *
 * Returns null, never an empty array, on any failure. That is the whole
 * contract: null means no server list reached this surface and the caller
 * falls back, while an empty array means the server found no focus competency
 * and the caller renders nothing. An environment where migration 258 has not
 * been applied yet answers with an error, so it falls back, which is why this
 * can ship ahead of the migration.
 */
export async function fetchServerFocusList(
  client: FocusListDataClient,
  sessionId: string | null | undefined,
): Promise<ServerFocusEntry[] | null> {
  if (!sessionId) return null;

  try {
    const result = await client
      .from(SERVER_FOCUS_LIST_VIEW)
      .select(FOCUS_LIST_COLUMNS)
      .eq('session_id', sessionId)
      .order('focus_rank', { ascending: true });

    if (result.error || !Array.isArray(result.data)) return null;
    return result.data as ServerFocusEntry[];
  } catch {
    return null;
  }
}
